import { ReactNode, useEffect, useState } from 'react';
import './user-agree.scss';
import { QRCodeSVG } from 'qrcode.react';
import { Spinner } from '../../../components/spinner/spinner';
import { Typography } from '../../../components/typography/typography';
import { Order, OrderSource, OrderStatus } from '../../../clients/quipli-service/order/order';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { useOrderApiV1 } from '../../../clients/quipli-service/order/order-api-v1';
import { NotificationType } from '../../../clients/quipli-service/notfication/notification';

export const UserAgree = ({ order }: { order: Order }) => {
  const { getMultipleConfigurations, sendNotification } = useAPI();
  const { checkUserHadAgreedToTerms, generateAcceptRentalTermsLink } = useOrderApiV1();

  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [userHasAgreed, setUserHasAgreed] = useState<boolean>();
  const [agreementMessage, setAgreementMessage] = useState<string>();
  const [isCheckingUserHasAgreed, setIsCheckingUserHasAgreed] = useState<boolean>(true);
  const [message, setMessage] = useState<ReactNode>('');
  const [enableSms, setEnableSms] = useState<boolean>(false);

  function refreshUserHasAgreed() {
    setIsCheckingUserHasAgreed(true);
    checkUserHadAgreedToTerms(order.id).then((res) => {
      setIsCheckingUserHasAgreed(false);
      setUserHasAgreed(res);
      if (res) {
        setMessage('');
      }
    });
  }

  useEffect(() => {
    const getMessage = async () => {
      const config = await getMultipleConfigurations([
        'CONF_CALCULATE_DELIVERY_USING_KM_GOOGLE_API',
        'CONF_REQUIRED_AGREE_TERMS',
      ]);

      if (
        [OrderSource.InStore, OrderSource.AdminPhone].includes(order.source) &&
        config.find((c) => c.variable === 'CONF_REQUIRED_AGREE_TERMS')?.value === '1'
      ) {
        setShowMessage(true);
        let message = 'User must agree to terms to approve the order.';

        if (
          (order.signature === undefined || order.signature === null) &&
          config?.find((c) => c.variable === 'CONF_CALCULATE_DELIVERY_USING_KM_GOOGLE_API')?.value === '1'
        ) {
          message = 'User must agree and sign to legal terms to approve the order.';

          if (order.status !== OrderStatus.Requested) {
            message = "User didn't agree or sign to legal terms yet.";
          }
        } else if (order.status !== OrderStatus.Requested) {
          message = "User didn't agree to legal terms yet.";
        }

        setAgreementMessage(message);
      } else {
        setShowMessage(false);
      }
    };

    getMessage();
  }, [order]);

  useEffect(() => {
    getMultipleConfigurations(['CONF_SEND_SMS']).then((config) => {
      if (config.find((c) => c.variable === 'CONF_SEND_SMS')?.value === '1' && order.user.phone !== '') {
        setEnableSms(true);
      }
    });

    refreshUserHasAgreed();
  }, []);

  async function openTermsInNetTab() {
    const link = await generateAcceptRentalTermsLink(order.id);
    window.open(link, '_blank');
  }

  async function generateTermsQRCode() {
    const link = await generateAcceptRentalTermsLink(order.id);
    setMessage(<QRCode link={link} t={Date.now()} />);
  }

  const refreshAgreeButton = (
    <div>
      {isCheckingUserHasAgreed ? (
        <Spinner />
      ) : (
        <button className="button-tertiary" onClick={refreshUserHasAgreed}>
          <i className="fa fa-refresh" />
        </button>
      )}
    </div>
  );

  return showMessage ? (
    <div className="user-agree">
      <Typography type="text-sm" className="agree-message text-quipli-black-800">
        <b>Note:</b>
        {userHasAgreed ? (
          'User has agreed to terms'
        ) : (
          <>
            {' '}
            {agreementMessage} {refreshAgreeButton}
          </>
        )}{' '}
      </Typography>
      {!userHasAgreed && (
        <>
          <ul>
            <ActionButton
              title="Resend email"
              action={() =>
                sendNotification({ type: NotificationType.acceptTerms, format: 'email', orderId: order.id }).then(() =>
                  setMessage(`Mail sent to ${order.user.email}.`),
                )
              }
              icon="fa-envelope"
            />
            {enableSms && (
              <ActionButton
                title="Resend SMS"
                action={async () => {
                  await sendNotification({ type: NotificationType.acceptTerms, format: 'sms', orderId: order.id });
                  setMessage(`SMS sent to ${order.user.name}.`);
                }}
                icon="fa-comment"
              />
            )}
            <ActionButton title="Sign on this device" action={openTermsInNetTab} icon="fa-pen" />
            <ActionButton title="Scan QR Code to sign" action={generateTermsQRCode} icon="fa-qrcode" />
          </ul>
          <span>{message}</span>
        </>
      )}
    </div>
  ) : null;
};

const ActionButton = ({ icon, action, title }: { icon: string; action: () => Promise<any>; title: string }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  return (
    <button
      disabled={isLoading}
      className="button-primary"
      title={title}
      onClick={() => {
        setIsLoading(true);
        action().then(() => setIsLoading(false));
      }}
    >
      {isLoading ? <Spinner /> : <i className={`fa ${icon}`} />}
    </button>
  );
};

const QRCode = ({ link, t }: { link: string; t: any }) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);

  useEffect(() => {
    setIsOpen(true);
  }, [t]);

  return isOpen ? (
    <div className="qr-code">
      <QRCodeSVG value={link} />
      <button className="button-tertiary" onClick={() => setIsOpen(false)}>
        <i className="fa fa-close"></i>
      </button>
    </div>
  ) : (
    <></>
  );
};
